import React, { useState, useEffect } from 'react';
import { getTokenUsage, getDailyTokenUsage } from '../services/api';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import Loader from '../components/common/Loader';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

interface TokenUsageData {
    total_requests: number;
    total_tokens: number;
    total_cost: number;
    services: Array<{
        service_type: string;
        request_count: number;
        total_tokens: number;
        total_cost: number;
        avg_cost_per_request: number;
    }>;
}

interface DailyUsageData {
    daily_usage: Array<{
        date: string;
        request_count: number;
        total_tokens: number;
        total_cost: number;
    }>;
}

const TokenUsageAnalytics: React.FC = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tokenUsage, setTokenUsage] = useState<TokenUsageData | null>(null);
    const [dailyUsage, setDailyUsage] = useState<DailyUsageData | null>(null);
    const [selectedPeriod, setSelectedPeriod] = useState(30);
    
    const clientId = localStorage.getItem('client_id');

    useEffect(() => {
        const fetchTokenUsageData = async () => {
            if (!clientId) {
                setError('No client ID found');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            try {
                const [usageData, dailyData] = await Promise.all([
                    getTokenUsage(clientId, selectedPeriod),
                    getDailyTokenUsage(clientId, 7)
                ]);
                
                console.log('Token usage data:', usageData);
                console.log('Daily usage data:', dailyData);
                
                setTokenUsage(usageData);
                setDailyUsage(dailyData);
                setError(null);
            } catch (err) {
                console.error('Error fetching token usage data:', err);
                const errorMessage = err instanceof Error ? err.message : 'Failed to fetch token usage data';
                setError(errorMessage);
                toast.error(errorMessage);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTokenUsageData();
    }, [clientId, selectedPeriod]);

    // Prepare chart data for service breakdown
    const serviceChartData = {
        labels: tokenUsage?.services?.map(service => service.service_type.charAt(0).toUpperCase() + service.service_type.slice(1)) || [],
        datasets: [
            {
                label: 'Cost ($)',
                data: tokenUsage?.services?.map(service => service.total_cost) || [],
                backgroundColor: [
                    'rgba(99, 102, 241, 0.8)',
                    'rgba(16, 185, 129, 0.8)',
                    'rgba(245, 158, 11, 0.8)',
                    'rgba(239, 68, 68, 0.8)',
                    'rgba(139, 92, 246, 0.8)'
                ],
                borderColor: [
                    'rgba(99, 102, 241, 1)',
                    'rgba(16, 185, 129, 1)',
                    'rgba(245, 158, 11, 1)',
                    'rgba(239, 68, 68, 1)',
                    'rgba(139, 92, 246, 1)'
                ],
                borderWidth: 1,
            },
        ],
    };

    // Prepare chart data for daily usage
    const dailyChartData = {
        labels: dailyUsage?.daily_usage?.map(day => 
            new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        ) || [],
        datasets: [
            {
                label: 'Daily Cost ($)',
                data: dailyUsage?.daily_usage?.map(day => day.total_cost) || [],
                backgroundColor: 'rgba(99, 102, 241, 0.8)',
                borderColor: 'rgba(99, 102, 241, 1)',
                borderWidth: 1,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top' as const,
            },
            title: {
                display: true,
                text: 'Token Usage Analytics',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: function(value: any) {
                        return '$' + value.toFixed(4);
                    }
                }
            }
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error Loading Token Usage Data</h3>
                            <div className="mt-2 text-sm text-red-700">
                                <p>{error}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="mb-8">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Token Usage Analytics</h1>
                        <p className="mt-2 text-sm text-gray-600">
                            Track AI API usage costs and token consumption across all services
                        </p>
                    </div>
                    <div className="flex items-center space-x-4">
                        <select
                            value={selectedPeriod}
                            onChange={(e) => setSelectedPeriod(Number(e.target.value))}
                            className="block w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                            <option value={7}>Last 7 days</option>
                            <option value={30}>Last 30 days</option>
                            <option value={90}>Last 90 days</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                                    <span className="text-white text-sm font-medium">$</span>
                                </div>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Total Cost</dt>
                                    <dd className="text-lg font-medium text-gray-900">
                                        ${tokenUsage?.total_cost?.toFixed(4) || '0.0000'}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                                    <span className="text-white text-sm font-medium">T</span>
                                </div>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Total Tokens</dt>
                                    <dd className="text-lg font-medium text-gray-900">
                                        {tokenUsage?.total_tokens?.toLocaleString() || '0'}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                                    <span className="text-white text-sm font-medium">R</span>
                                </div>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Total Requests</dt>
                                    <dd className="text-lg font-medium text-gray-900">
                                        {tokenUsage?.total_requests?.toLocaleString() || '0'}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                                    <span className="text-white text-sm font-medium">Avg</span>
                                </div>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Avg Cost/Request</dt>
                                    <dd className="text-lg font-medium text-gray-900">
                                        ${tokenUsage?.total_requests ? (tokenUsage.total_cost / tokenUsage.total_requests).toFixed(4) : '0.0000'}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* Service Breakdown Chart */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Cost by Service Type</h3>
                    {tokenUsage?.services && tokenUsage.services.length > 0 ? (
                        <div className="h-64">
                            <Pie data={serviceChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                        </div>
                    ) : (
                        <div className="h-64 flex items-center justify-center text-gray-500">
                            No service data available
                        </div>
                    )}
                </div>

                {/* Daily Usage Chart */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Daily Usage Trend</h3>
                    {dailyUsage?.daily_usage && dailyUsage.daily_usage.length > 0 ? (
                        <div className="h-64">
                            <Bar data={dailyChartData} options={{ ...chartOptions, maintainAspectRatio: false }} />
                        </div>
                    ) : (
                        <div className="h-64 flex items-center justify-center text-gray-500">
                            No daily usage data available
                        </div>
                    )}
                </div>
            </div>

            {/* Service Details Table */}
            <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Service Usage Details</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Service Type
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Requests
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Total Tokens
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Total Cost
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Avg Cost/Request
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {tokenUsage?.services?.map((service, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            <span className="capitalize">{service.service_type}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {service.request_count.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {service.total_tokens.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            ${service.total_cost.toFixed(4)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            ${service.avg_cost_per_request.toFixed(4)}
                                        </td>
                                    </tr>
                                )) || (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                                            No service usage data available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TokenUsageAnalytics;